import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Badge, { PosBadge, LeadStatusBadge } from '../components/Badge.jsx';
import OutreachStageDot, { stageTextColor } from '../components/OutreachStageDot.jsx';
import FollowUpCard from '../components/FollowUpCard.jsx';
import ColumnToggle, { LayoutDropdown, useColumnPreferences } from '../components/ColumnToggle.jsx';
import ColumnFilterMenu from '../components/ColumnFilterMenu.jsx';
import useColumnFilters from '../components/useColumnFilters.js';
import RecentImportBanner from '../components/RecentImportBanner.jsx';
import BatchEmailDialog from '../components/BatchEmailDialog.jsx';

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function contactSubtext(l) {
  const parts = [l.first_name, l.last_name].filter(Boolean).join(' ');
  return parts || l.position || '';
}

function daysBetween(targetISO, todayISO) {
  if (!targetISO) return null;
  const a = new Date(targetISO + 'T00:00:00');
  const b = new Date(todayISO + 'T00:00:00');
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
}

function probabilityTone(p) {
  if (p == null) return 'grey';
  if (p >= 70) return 'green';
  if (p >= 30) return 'amber';
  return 'red';
}

function NextContactCell({ value, todayISO }) {
  const formatted = formatDate(value);
  if (!formatted) return '';
  const days = daysBetween(formatted, todayISO);
  let dotClass = 'status-dot-grey';
  if (days != null) {
    if (days > 0) dotClass = 'status-dot-red';
    else if (days === 0) dotClass = 'status-dot-amber';
    else dotClass = 'status-dot-green';
  }
  return (
    <>
      <span className={`status-dot ${dotClass}`} />
      <span className="cell-mono">{formatted}</span>
    </>
  );
}

function ProbabilityBadge({ value }) {
  if (value == null) return <span className="cell-muted">—</span>;
  const tone = probabilityTone(value);
  return <span className={`badge badge-${tone}`}>{value}%</span>;
}

function truncateCell(s, max = 24) {
  if (!s) return '';
  const v = String(s);
  return v.length > max ? v.slice(0, max - 1) + '…' : v;
}

// Column registry — same five-group taxonomy as the Customers list,
// plus a Pipeline group for lead-only fields (close probability,
// expected close date, follow-up cadence, status). Site Details
// columns come from lead_details (leads don't have rows in the
// `sites` table — their operational details live on the lead row).
const COLUMNS = [
  // ── Contact Information ─────────────────────────────────────────
  {
    key: 'company', label: 'Company', group: 'contact',
    filterValue: (l) => l.company || '',
    cell: (l) => {
      const subtext = contactSubtext(l);
      // Stage color applies to the whole row's company name AND
      // adds weight (semibold) so the cadence is unmistakable from
      // across the page (Dave 2026-06-09: "makes it very noticeable").
      const stageColor = stageTextColor(l.outreach_stage);
      const style = stageColor
        ? { display: 'flex', alignItems: 'center', color: stageColor, fontWeight: 'var(--font-semibold)' }
        : { display: 'flex', alignItems: 'center' };
      return (
        <>
          <div className="cell-primary" style={style}>
            <OutreachStageDot stage={l.outreach_stage} />
            {l.company || '—'}
          </div>
          {subtext && <div className="cell-subtext">{subtext}</div>}
        </>
      );
    }
  },
  { key: 'first_name', label: 'First Name', group: 'contact', defaultHidden: true,
    cell: (l) => l.first_name || '' },
  { key: 'last_name', label: 'Last Name', group: 'contact', defaultHidden: true,
    cell: (l) => l.last_name || '' },
  { key: 'position', label: 'Position', group: 'contact', defaultHidden: true,
    filterValue: (l) => l.position || '',
    cell: (l) => l.position || '' },
  { key: 'current_status', label: 'Status', group: 'contact',
    filterValue: (l) => l.current_status || '',
    cell: (l) => l.current_status
      ? <LeadStatusBadge value={l.current_status} />
      : '—' },
  { key: 'last_email_status', label: 'Last Email', group: 'contact',
    filterValue: (l) => l.last_email_status || '(no email sent)',
    cell: (l) => {
      if (!l.last_email_status) return '—';
      const tone = l.last_email_status === 'Opened'    ? 'green'
                : l.last_email_status === 'Delivered'  ? 'teal'
                : l.last_email_status === 'Sent'       ? 'grey'
                : l.last_email_status === 'Bounced'    ? 'red'
                :                                        'grey';
      return <Badge tone={tone}>{l.last_email_status}</Badge>;
    },
  },
  { key: 'website', label: 'Website', group: 'contact', defaultHidden: true,
    tdClass: 'cell-mono',
    cell: (l) => truncateCell(l.website, 32) },

  // ── Address ─────────────────────────────────────────────────────
  { key: 'address1', label: 'Address 1', group: 'address', defaultHidden: true,
    cell: (l) => truncateCell(l.address1, 32) },
  { key: 'address2', label: 'Address 2', group: 'address', defaultHidden: true,
    cell: (l) => truncateCell(l.address2, 32) },
  { key: 'city', label: 'City', group: 'address',
    filterValue: (l) => l.city || '',
    cell: (l) => l.city || '' },
  { key: 'province', label: 'Province', group: 'address',
    filterValue: (l) => l.province_state || '',
    cell: (l) => l.province_state || '' },
  // Owner column sits between Province and Fuel Brand per Dave's ask
  // (2026-06-09): keeps the "who's working this" signal in the
  // location/site cluster where reps glance. The 'pipeline' group key
  // is preserved so existing ColumnToggle preferences keep working;
  // only the source-order position moved.
  { key: 'owner', label: 'Owner', group: 'pipeline',
    filterValue: (l) => l.owner_name || '(unassigned)',
    cell: (l) => l.owner_name
      ? <Badge tone="teal">{l.owner_name}</Badge>
      : <span className="cell-muted">unassigned</span> },
  { key: 'postal_zip', label: 'Postal / Zip', group: 'address', defaultHidden: true,
    tdClass: 'cell-mono',
    cell: (l) => l.postal_zip || '' },
  { key: 'country', label: 'Country', group: 'address', defaultHidden: true,
    filterValue: (l) => l.country || '',
    cell: (l) => l.country || '' },

  // ── Phone & Email ───────────────────────────────────────────────
  { key: 'phone_main', label: 'Phone Main', group: 'phoneEmail', tdClass: 'cell-mono',
    cell: (l) => l.phone_main || '' },
  { key: 'phone_cell', label: 'Phone Cell', group: 'phoneEmail', defaultHidden: true,
    tdClass: 'cell-mono',
    cell: (l) => l.phone_cell || '' },
  { key: 'phone_fax', label: 'Phone Fax', group: 'phoneEmail', defaultHidden: true,
    tdClass: 'cell-mono',
    cell: (l) => l.phone_fax || '' },
  { key: 'email', label: 'Email', group: 'phoneEmail',
    cell: (l) => <div className="cell-primary cell-mono">{l.email1 || ''}</div> },
  { key: 'email2', label: 'Email 2', group: 'phoneEmail', defaultHidden: true,
    tdClass: 'cell-mono',
    cell: (l) => l.email2 || '' },
  { key: 'email3', label: 'Email 3', group: 'phoneEmail', defaultHidden: true,
    tdClass: 'cell-mono',
    cell: (l) => l.email3 || '' },

  // ── Site Details (from lead_details) ────────────────────────────
  { key: 'site_number', label: 'Site Number', group: 'site',
    cell: (l) => <span className="cell-mono">{l.site_number || ''}</span> },
  { key: 'pos_type', label: 'POS Type', group: 'site',
    filterValue: (l) => l.pos_type || '',
    cell: (l) => <PosBadge value={l.pos_type} /> },
  { key: 'fuel_brand', label: 'Fuel Brand', group: 'site',
    filterValue: (l) => l.fuel_brand || '',
    cell: (l) => l.fuel_brand || '' },
  { key: 'install_date', label: 'Install Date', group: 'site',
    tdClass: 'cell-mono', defaultHidden: true,
    cell: (l) => formatDate(l.install_date) },

  // ── Pipeline (lead-only) ────────────────────────────────────────
  // Owner moved up to the address group (between Province and
  // Fuel Brand) -- see definition above.
  { key: 'close_probability', label: 'Close Probability', group: 'pipeline',
    cell: (l) => <ProbabilityBadge value={l.close_probability} /> },
  { key: 'expected_close_date', label: 'Expected Close Date', group: 'pipeline',
    tdClass: 'cell-mono', cell: (l) => formatDate(l.expected_close_date) },
  { key: 'next_contact', label: 'Next Contact', group: 'pipeline',
    cell: (l, ctx) => <NextContactCell value={l.next_contact} todayISO={ctx.todayISO} /> },
  { key: 'distributor', label: 'Distributor', group: 'pipeline', defaultHidden: true,
    filterValue: (l) => l.distributor || '',
    cell: (l) => l.distributor || '' },
  { key: 'file_type', label: 'File Type', group: 'pipeline', defaultHidden: true,
    filterValue: (l) => l.file_type || '',
    cell: (l) => l.file_type || '' },
  { key: 'last_contact', label: 'Last Contact', group: 'pipeline',
    tdClass: 'cell-mono', defaultHidden: true,
    cell: (l) => formatDate(l.last_contact) },

  // ── Credentials (sensitive — opt-in) ────────────────────────────
  { key: 'username_password', label: 'Username / Password (legacy)', group: 'credentials',
    tdClass: 'cell-mono', defaultHidden: true,
    cell: (l) => truncateCell(l.username_password) }
];
const DEFAULT_KEYS = COLUMNS.filter((c) => !c.defaultHidden).map((c) => c.key);
const ALL_KEYS     = COLUMNS.map((c) => c.key);

// Probability buckets for the tab nav at the top of the page. Bands
// align with the dashboard's "Follow-up Today" cutoff (>= 80%): the
// "Closing" tab here shows exactly the same set the dashboard surfaces,
// so Dave never has to translate between the two views.
const PROB_BUCKETS = [
  { id: 'all',     label: 'All',     tone: 'grey',
    match: (_l) => true },
  { id: 'cold',    label: 'Cold',    tone: 'blue',
    match: (l) => l.close_probability != null && l.close_probability >= 1  && l.close_probability <= 29 },
  { id: 'warm',    label: 'Warm',    tone: 'amber',
    match: (l) => l.close_probability != null && l.close_probability >= 30 && l.close_probability <= 49 },
  { id: 'hot',     label: 'Hot',     tone: 'orange',
    match: (l) => l.close_probability != null && l.close_probability >= 50 && l.close_probability <= 79 },
  { id: 'closing', label: 'Closing', tone: 'green',
    match: (l) => l.close_probability != null && l.close_probability >= 80 && l.close_probability <= 100 },
  // Status-based (not probability): fresh inbound off the website demo
  // form. Drops off this tab once David works it and changes the status.
  { id: 'website', label: 'New Website Leads', tone: 'teal',
    match: (l) => l.current_status === 'Website Lead' }
];

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [batchOpen, setBatchOpen] = useState(false);
  // Active probability bucket — drives which leads show in the top
  // panel and (if not 'all') also filters the main table below.
  const [probBucket, setProbBucket] = useState('all');
  const navigate = useNavigate();
  const todayISO = new Date().toISOString().slice(0, 10);
  const {
    visibleKeys, setVisibleKeys,
    layouts, activeName, setActiveName,
    saveLayout, deleteLayout
  } = useColumnPreferences('leads', DEFAULT_KEYS, ALL_KEYS);

  const {
    filters, setFilter, clearFilter, clearAll,
    activeCount, valuesByKey, applyFilters
  } = useColumnFilters(leads, COLUMNS);

  async function loadLeads() {
    try {
      const res = await axios.get('/api/leads');
      setLeads(res.data.leads || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadLeads(); }, []);

  // Count of active leads in each probability bucket, for the badges
  // shown on each tab. Excludes not-interested leads from the count
  // because they don't show up in the main table either.
  const bucketCounts = useMemo(() => {
    const counts = { all: 0, cold: 0, warm: 0, hot: 0, closing: 0, website: 0 };
    for (const l of leads) {
      if (l.not_interested) continue;
      counts.all++;
      if (l.current_status === 'Website Lead') counts.website++;
      const p = l.close_probability;
      if (p == null) continue;
      if      (p <=  29) counts.cold++;
      else if (p <=  49) counts.warm++;
      else if (p <=  79) counts.hot++;
      else                counts.closing++;
    }
    return counts;
  }, [leads]);

  // Leads shown in the top panel for the currently-active bucket.
  // Sorted by close_probability desc (highest first within the bucket),
  // then by oldest next_contact so the most-overdue float to the top.
  const bucketLeads = useMemo(() => {
    const bucket = PROB_BUCKETS.find((b) => b.id === probBucket);
    if (!bucket) return [];
    return leads
      .filter((l) => !l.not_interested && bucket.match(l))
      .sort((a, b) => {
        const pa = a.close_probability ?? -1;
        const pb = b.close_probability ?? -1;
        if (pb !== pa) return pb - pa;
        const da = formatDate(a.next_contact);
        const db = formatDate(b.next_contact);
        return da.localeCompare(db);
      });
  }, [leads, probBucket]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Bucket filter — when a non-"All" probability tab is active,
    // restrict the table to leads in that band too. Keeps the top
    // panel and the main table consistent with each other.
    const bucket = PROB_BUCKETS.find((b) => b.id === probBucket);
    const byBucket = (bucket && bucket.id !== 'all')
      ? leads.filter((l) => !l.not_interested && bucket.match(l))
      : leads;
    const byQuery = !q ? byBucket : byBucket.filter((l) => {
      const haystack = [
        l.company, l.first_name, l.last_name, l.position, l.site_number, l.pos_type,
        l.city, l.province_state, l.phone_main, l.phone_cell, l.email1, l.fuel_brand,
        l.distributor, l.file_type,
        l.current_status, l.last_email_status,
        formatDate(l.expected_close_date), formatDate(l.next_contact),
        l.close_probability != null ? `${l.close_probability}%` : ''
      ].map((v) => (v == null ? '' : String(v).toLowerCase())).join(' | ');
      return haystack.includes(q);
    });
    return applyFilters(byQuery);
  }, [leads, query, probBucket, applyFilters]);

  return (
    <section className="page">
      <div className="page-header">
        <h2>Sales Leads</h2>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          {selectedIds.size > 0 && (
            <>
              <button type="button" className="btn-primary" onClick={() => setBatchOpen(true)}>
                📧 Email {selectedIds.size} Selected
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={async () => {
                  if (!window.confirm(
                    `Delete ${selectedIds.size} lead(s) permanently?\n\nThis removes the contact + all related data ` +
                    `(lead details, notes, documents, subscriptions). Cannot be undone — but if these are recent imports, ` +
                    `you can use Settings → Import History to undo the batch instead.`
                  )) return;
                  try {
                    const r = await axios.post('/api/leads/bulk-delete', { lead_ids: [...selectedIds] });
                    alert(`Deleted ${r.data.deleted} of ${r.data.requested} leads.`);
                    setSelectedIds(new Set());
                    loadLeads();
                  } catch (e) {
                    alert(`Delete failed: ${e.response?.data?.error || e.message}`);
                  }
                }}
              >
                🗑 Delete {selectedIds.size} Selected
              </button>
            </>
          )}
          <Link to="/leads/import" className="btn-secondary">Import CSV</Link>
          <Link to="/leads/new" className="btn-primary">+ Add New Lead</Link>
        </div>
      </div>

      {error && <p className="error">Error: {error}</p>}

      <RecentImportBanner recordType="lead" onUndone={loadLeads} />

      {/* Probability bucket tabs — replaces the old single "Follow-Up
          Required" panel. Switching tabs filters BOTH this card grid
          AND the main leads table below (when bucket !== 'all'). The
          dashboard's Follow-up Today panel mirrors what's in "Closing". */}
      <div className="followup-panel">
        <div className="followup-header">
          <h3>Pipeline by probability</h3>
          <span className="followup-count">
            {bucketLeads.length} lead{bucketLeads.length === 1 ? '' : 's'} in this bucket · ranked by probability then overdue
          </span>
        </div>

        <div
          className="probability-tabs"
          role="tablist"
          aria-label="Filter leads by close probability"
          style={{
            display: 'flex',
            gap: 'var(--space-1)',
            borderBottom: 'var(--border-width) solid var(--color-border)',
            marginBottom: 'var(--space-3)'
          }}
        >
          {PROB_BUCKETS.map((b) => {
            const isActive = b.id === probBucket;
            const count = bucketCounts[b.id] ?? 0;
            return (
              <button
                key={b.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setProbBucket(b.id)}
                style={{
                  padding: 'var(--space-2) var(--space-3)',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: isActive
                    ? `2px solid var(--color-accent)`
                    : '2px solid transparent',
                  marginBottom: '-1px',
                  cursor: 'pointer',
                  fontSize: 'var(--text-body)',
                  fontWeight: isActive ? 'var(--font-semibold)' : 'var(--font-regular)',
                  color: isActive ? 'var(--color-text)' : 'var(--color-text-dim)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)'
                }}
              >
                <span>{b.label}</span>
                <Badge tone={b.tone}>{count}</Badge>
                {b.min != null && (
                  <span style={{
                    fontSize: 'var(--text-small)',
                    color: 'var(--color-text-faint)',
                    fontFamily: 'var(--font-mono)'
                  }}>
                    {b.min}–{b.max}%
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {bucketLeads.length === 0 ? (
          <p className="empty-tab" style={{ margin: 'var(--space-3) 0' }}>
            No leads in this bucket.
          </p>
        ) : (
          <div className="followup-grid">
            {bucketLeads.slice(0, 12).map((l) => (
              <FollowUpCard
                key={l.id}
                lead={l}
                todayISO={todayISO}
                onClick={() => navigate(`/leads/${l.id}`)}
              />
            ))}
          </div>
        )}
        {bucketLeads.length > 12 && (
          <p className="modal-sub" style={{ marginTop: 'var(--space-2)', textAlign: 'center' }}>
            Showing top 12 of {bucketLeads.length} — see the full filtered list in the table below.
          </p>
        )}
      </div>

      <div className="toolbar">
        <input
          type="search"
          className="search-input"
          placeholder="Search company, contact, site, status, distributor…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <span className="result-count">
          {loading ? 'Loading…' : `${filtered.length} of ${leads.length}`}
        </span>
        {activeCount > 0 && (
          <button type="button" className="btn-secondary btn-sm" onClick={clearAll}>
            Clear Filters ({activeCount})
          </button>
        )}
        <div className="toolbar-prefs">
          <LayoutDropdown
            layouts={layouts}
            activeName={activeName}
            onChange={setActiveName}
          />
          <ColumnToggle
            columns={COLUMNS.map(({ key, label, group }) => ({ key, label, group }))}
            value={visibleKeys}
            onChange={setVisibleKeys}
            activeName={activeName}
            onSaveAs={(name) => saveLayout(name, visibleKeys)}
            onDelete={deleteLayout}
          />
        </div>
      </div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 32, paddingRight: 0 }}>
                <input
                  type="checkbox"
                  checked={filtered.length > 0 && filtered.every((l) => selectedIds.has(l.id))}
                  ref={(el) => {
                    if (!el) return;
                    const all = filtered.length > 0 && filtered.every((l) => selectedIds.has(l.id));
                    const some = filtered.some((l) => selectedIds.has(l.id));
                    el.indeterminate = !all && some;
                  }}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedIds(new Set([...selectedIds, ...filtered.map((l) => l.id)]));
                    } else {
                      const next = new Set(selectedIds);
                      filtered.forEach((l) => next.delete(l.id));
                      setSelectedIds(next);
                    }
                  }}
                  aria-label="Select all visible leads"
                />
              </th>
              {COLUMNS.filter((c) => visibleKeys.includes(c.key)).map((c) => {
                const filterable = typeof c.filterValue === 'function';
                const isActive = filterable && !!filters[c.key];
                return (
                  <th key={c.key} className={isActive ? 'th-filter-active' : ''}>
                    <span className="th-label">{c.label}</span>
                    {filterable && (
                      <ColumnFilterMenu
                        label={c.label}
                        values={valuesByKey[c.key] || []}
                        selected={filters[c.key]}
                        onChange={(set) => setFilter(c.key, set)}
                        onClear={() => clearFilter(c.key)}
                      />
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={visibleKeys.length + 1} className="empty-row">
                  {leads.length === 0 ? 'No leads yet.' : 'No leads match your search.'}
                </td>
              </tr>
            )}
            {filtered.map((l) => (
              <tr
                key={l.id}
                onClick={() => navigate(`/leads/${l.id}`)}
                className="row-clickable"
              >
                <td style={{ width: 32, paddingRight: 0 }} onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(l.id)}
                    onChange={(e) => {
                      const next = new Set(selectedIds);
                      if (e.target.checked) next.add(l.id); else next.delete(l.id);
                      setSelectedIds(next);
                    }}
                    aria-label={`Select ${l.company || 'lead'}`}
                  />
                </td>
                {COLUMNS.filter((c) => visibleKeys.includes(c.key)).map((c) => (
                  <td key={c.key} className={c.tdClass || ''}>
                    {c.cell(l, { todayISO })}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <BatchEmailDialog
        open={batchOpen}
        leadIds={[...selectedIds]}
        onClose={() => setBatchOpen(false)}
        onSent={() => {
          setBatchOpen(false);
          setSelectedIds(new Set());
          loadLeads();
        }}
      />
    </section>
  );
}
